import { createApi } from "@reduxjs/toolkit/query/react";

import { AIKittyType, Assistant } from "../../features/bot/types";
import { IPPAssistantBotCreateRequest } from "../../interfaces/request/assistant-bot-create-request.interface";
import {
  GenerateUploadUrlResponse,
  ICdnInfoResponse,
  IPPAssistantBotUpdateRequest,
  IValidateUploadedItem
} from "../../interfaces/request/assistant-bot-update-request.interface";
import IUpdateWidgetSettingRequest from "../../interfaces/request/update-widget-setting-request.interface";
import axiosBaseQuery, { getToken } from "../root";
import {
  IGetAllScenariosResponse,
  IKittyCreateModelRequest,
  McFaceCreateModelRequest
} from "./chatbot-rtk.type";

const baseUrl = `${import.meta.env.VITE_API_DOMAIN ?? "/api"}`;

const baseQuery = axiosBaseQuery({
  baseUrl
});

interface IEditAssistant extends AIKittyType {
  id: string;
}

interface IEditkbAssistant extends AIKittyType {
  id: string;
}

export const ChatbotRTKService = createApi({
  reducerPath: "ChatbotRTKService",
  baseQuery,
  tagTypes: [
    "Package",
    "GetAllScenarios",
    "CreateModel",
    "GetScript",
    "GetTextScenarioStarterKits",
    "GetAssistantDetail",
    "UrlChecker",
    "GetBusinessPartner",
    "ConversationHisyory"
  ],
  endpoints: (builder) => ({
    getPackage: builder.query<any, void>({
      query: () => ({
        url: `/v3/client`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["Package"],
      transformResponse: (response: any) => response.element
    }),
    getAllScenarios: builder.query<
      IGetAllScenariosResponse[],
      { botType: "widget" | "link" }
    >({
      query: ({ botType }) => ({
        url: `/v3/knowledgeBase?available_for=${botType || "widget"}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: (result, error, { botType }) => [
        { type: "GetAllScenarios", botType }
      ],
      transformResponse: (response: any) => response.element
    }),
    getAssistants: builder.query<Assistant[], { language: string }>({
      query: ({ language }) => ({
        url: `/server/assistant/list?language=${language}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["GetAllScenarios"],
      transformResponse: (response: any) => response.element
    }),
    getScript: builder.query<any, string>({
      query: (id) => ({
        url: `/server/client/script/${id}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["GetScript"],
      transformResponse: (response: any) => response.element
    }),
    getkittyModels: builder.query<any, void>({
      query: () => ({
        url: `/v3/fineTune/models`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getAssistantModels: builder.query<any, void>({
      query: () => ({
        url: `/server/assistant/models`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getMcBot: builder.query<any, void>({
      query: () => ({
        url: `/server/mcbot`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getBots: builder.query<any, void>({
      query: () => ({
        url: `/v3/bot/versions`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        },
        keepUnusedDataFor: 3600
      }),
      transformResponse: (response: any) => response.element
    }),
    createModel: builder.mutation<any, IKittyCreateModelRequest>({
      query: (data) => ({
        url: `/v3/fineTune/create`,
        method: "POST",
        data: data,

        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progress: any) => {
          const { loaded, total } = progress;
          const percentageProgress = Math.floor((loaded / total) * 100);
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["GetAllScenarios", "Package"]
    }),
    uploadWidgetImage: builder.mutation<any, { data: any }>({
      query: (data) => ({
        url: `/server/client/setting/upload-widget-image`,
        method: "POST",
        data: {
          file: data
        },
        formData: true,
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progress: any) => {
          const { loaded, total } = progress;
          const percentageProgress = Math.floor((loaded / total) * 100);
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["GetAllScenarios", "Package"]
    }),
    setDefaultScenarios: builder.mutation<any, string>({
      query: (data) => ({
        url: `/v3/knowledgeBase/setDefault/${data}`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["GetAllScenarios", "GetScript", "Package"]
    }),
    checkApiKey: builder.mutation<any, string>({
      query: (data) => ({
        url: `/v3/client/updateOpenAISetting`,
        method: "PATCH",
        data: {
          aikitty_defaults: {
            apiKey: data
          }
        },
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    checkIkittyStatus: builder.mutation<any, string[]>({
      query: (data) => ({
        url: `/server/kitty/check-status`,
        method: "PUT",
        data: { tuneIds: data },
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    createMcFaceModel: builder.mutation<any, McFaceCreateModelRequest>({
      query: (data) => ({
        url: `/server/mcface/scenario furnace`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    createAssistantBot: builder.mutation<any, AIKittyType>({
      query: (data) => ({
        url: `/v3/assistant/create`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    editAssistantBot: builder.mutation<any, IEditAssistant>({
      query: (data) => ({
        url: `/v3/assistant/update/${data.id}`,
        method: "PATCH",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    getAssistantDetail: builder.mutation<any, string>({
      query: (id) => ({
        url: `/v3/assistant/detail/${id}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    createKbAssistantBot: builder.mutation<any, AIKittyType>({
      query: (data) => ({
        url: `/v3/kb-assistant/create`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    editKbAssistantBot: builder.mutation<any, IEditkbAssistant>({
      query: (data) => ({
        url: `/v3/kb-assistant/update/${data.id}`,
        method: "PATCH",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    addFileKbAssistantBot: builder.mutation<any, { data: any; id: string }>({
      query: (data) => ({
        url: `/v3/kb-assistant/upload/${data.id}`,
        method: "PATCH",
        data: data.data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      // invalidatesTags: ["GetAllScenarios", "Package"]
      invalidatesTags: []
    }),
    addKbAssistantBot: builder.mutation<
      any,
      {
        data: {
          original_name: string;
          cdn_name: string;
          cdn_link: string;
          bot_code: string;
          title?: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v3/kb-assistant/update-config/${data.id}`,
        method: "PATCH",
        data: data.data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: []
    }),
    deleteFileKbAssistantBot: builder.mutation<
      any,
      { data: any; bot_id: string }
    >({
      query: (data) => ({
        url: `/v3/kb-assistant/delete/${data.bot_id}`,
        method: "DELETE",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      // fixedCacheKey: "aiKnowledgeFileDeleteMutator",
      transformResponse: (response) => response.element,
      // invalidatesTags: ["GetAllScenarios", "Package"]
      invalidatesTags: []
    }),
    getKbAssistantDetail: builder.mutation<any, number>({
      query: (id) => ({
        url: `/v3/kb-assistant/detail/${id}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),

    deleteScenario: builder.mutation<any, string>({
      query: (data) => ({
        url: `/v3/knowledgeBase/delete/${data}`,
        method: "DELETE",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    knowledgeBaseDuplicate: builder.mutation<any, string>({
      query: (data) => ({
        url: `/v3/knowledgeBase/duplicate/${data}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    updateSetting: builder.mutation<
      any,
      { apiKey?: string; maxToken: number; model: string }
    >({
      query: (data) => ({
        url: `/v3/client/updateOpenAISetting`,
        method: "PATCH",
        data: {
          aikitty_defaults: { ...data }
        },
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    updateWidgetSetting: builder.mutation<any, any>({
      query: (data) => ({
        url: `/v3/client/updateWidgetSetting`,
        method: "PATCH",
        data: data,
        headers: {
          "x-auth-token": getToken()
          // "Content-Type": "multipart/form-data"
        }
        // onUploadProgress: (progress: any) => {
        //   const { loaded, total } = progress;
        //   const percentageProgress = Math.floor((loaded / total) * 100);
        // }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["GetAllScenarios", "Package"]
    }),
    updateLinkSetting: builder.mutation<any, IUpdateWidgetSettingRequest>({
      query: (data) => ({
        url: `/v3/client/updateLinkSetting`,
        method: "PATCH",
        data: data,
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progress: any) => {
          const { loaded, total } = progress;
          const percentageProgress = Math.floor((loaded / total) * 100);
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    UploadToCDN: builder.mutation<any, any>({
      query: (data) => ({
        url: `/v3/cdn/upload`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progress: any) => {
          const { loaded, total } = progress;
          const percentageProgress = Math.floor((loaded / total) * 100);
        }
      }),
      transformResponse: (response) => response.element
    }),
    createContactUs: builder.mutation<any, { message: string; email: string }>({
      query: (data) => ({
        url: `/server/client/contact-us`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response
    }),
    getSiteMapOfDomainUrl: builder.mutation<any, string>({
      query: (url) => ({
        url: `/v3/pp-assistant/sitemap?url=${url}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element
    }),
    createPPAssistantBot: builder.mutation<any, IPPAssistantBotCreateRequest>({
      query: (data) => ({
        url: `/v3/pp-assistant/create`,
        method: "POST",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    editPPAssistantBot: builder.mutation<any, IPPAssistantBotUpdateRequest>({
      query: (data) => ({
        url: `/v3/pp-assistant/update/${data.id}`,
        method: "PATCH",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    }),
    // Following api is for getting chat text scenario starter kits
    getTextScenarioStarterKits: builder.query<any, string>({
      query: (botCode) => ({
        url: `/v3/scenario/templates/${botCode}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    // Following api simulates voice popover fetch data
    getVoicePopover: builder.query<string, string>({
      queryFn(arg, queryApi, extraOptions, baseQuery) {
        return new Promise((resolve, reject) => {
          resolve({ data: arg });
        });
      }
    }),
    getNotificationSounds: builder.query<any, void>({
      query: () => ({
        url: `/notification-sounds`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    // Following api will update appearance for targeted widget (AiKitty)
    updateAiWidgetAppearance: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/v3/knowledgeBase/settings/${id}`,
        method: "PATCH",
        data,
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) => (result ? ["GetAllScenarios"] : [])
    }),
    // Following api will update appearance for targeted widget (McBot)
    updateMcBotWidgetAppearance: builder.mutation<
      any,
      { id: string; data: any }
    >({
      query: ({ id, data }) => ({
        url: `/v3/scenario/settings/${id}`,
        method: "PATCH",
        data,
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "multipart/form-data"
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) => (result ? ["GetAllScenarios"] : [])
    }),
    // Following  api does the deactivation process for AiKitty
    deactivateAiType: builder.mutation<any, string>({
      query: (id) => ({
        url: `/v3/knowledgeBase/deactivate/${id}`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) => (result ? ["GetAllScenarios"] : [])
    }),
    // Following  api does the deactivation process for AiKitty
    deactivateMcBotType: builder.mutation<any, string>({
      query: (id) => ({
        url: `/v3/scenario/deactivate/${id}`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) => (result ? ["GetAllScenarios"] : [])
    }),
    urlChecker: builder.query<any, string>({
      query: (url) => ({
        url: `/url-checker?url=${url}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element,
      providesTags: (result, error, arg) =>
        result
          ? [
              {
                type: "UrlChecker",
                id: arg
              }
            ]
          : []
    }),
    updateGlobalPoweredBy: builder.mutation<any, { poweredBy: boolean }>({
      query: ({ poweredBy }) => ({
        url: `/v3/client/updateGlobalPoweredBy`,
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data: {
          poweredBy
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) => (result ? ["Package"] : [])
    }),
    getBusinessPartner: builder.query<any, any>({
      query: () => ({
        url: `/partner/business-partner/info`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["GetBusinessPartner"],
      transformResponse: (response) => response.element
    }),
    generateUploadUrl: builder.mutation<
      GenerateUploadUrlResponse,
      { fileName: string; isPrivate: boolean }
    >({
      query: ({ fileName, isPrivate }) => ({
        url: "/v3/cdn/generate-upload-url",
        method: "POST",
        data: { file_name: fileName, is_private: isPrivate },
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "application/json"
        }
      }),
      transformResponse: (response: any) => response.element // Return the `element` as the transformed response
    }),
    getCdnFileInfo: builder.mutation<
      ICdnInfoResponse,
      { fileName: string; sizeMB: number }
    >({
      query: ({ fileName, sizeMB }) => ({
        url: "/v3/cdn/info",
        params: { fileName, sizeMB },
        method: "GET",
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "application/json"
        }
      })
    }),
    validateUploadedItem: builder.mutation<
      IValidateUploadedItem,
      {
        file_name: string;
      }
    >({
      query: ({ file_name }) => ({
        url: "/v3/cdn/validate",
        method: "POST",
        data: {
          file_name,
          size_range: [100, 500000000], // File size range in bytes (100B to 500MB)
          extensions: [
            "avif",
            "gif",
            "jpeg",
            "jpg",
            "json",
            "jsonl",
            "mp3",
            "mp4",
            "pdf",
            "png",
            "svg",
            "webp",
            "css",
            "js",
            "xml",
            "wav",
            "tsx",
            "ts",
            "jsx",
            "heif",
            "heic",
            "mov",
            "avi",
            "hevc",
            "ttf",
            "otf",
            "woff",
            "eot",
            "webm",
            "ogg",
            "txt",
            "mkv",
            "flv",
            "wmv",
            "m4v",
            "m4a",
            "3gp",
            "mpeg",
            "aac",
            "doc",
            "docx",
            "rtf",
            "xls",
            "xlsx",
            "csv",
            "ppt",
            "pptx",
            "zip",
            "rar",
            "md",
            "html",
            "md"
          ],
          is_private: true // Optional, defaults to false
        },
        headers: {
          "x-auth-token": getToken(),
          "Content-Type": "application/json"
        }
      }),
      transformResponse: (response: any) => response.element // Return the `element` as the transformed response
    }),
    getBusinessPartnerUsers: builder.query<
      {
        users: any[];
        metaData: {
          totlal: number;
          totalPage: number;
          hasPreviousePage: boolean;
          hasNextPage: boolean;
          page: number;
          limit: number;
        };
      },
      | number
      | undefined
      | {
          limit: number;
          offset: number;
        }
    >({
      query: (days) => ({
        url: `/partner/business-partner/users${
          days && typeof days === "number"
            ? `?days=${days}`
            : typeof days === "object"
            ? `?limit=${
                (
                  days as {
                    limit: number;
                    offset: number;
                  }
                ).limit
              }&page=${
                (
                  days as {
                    limit: number;
                    offset: number;
                  }
                ).offset
              }&days=${
                (
                  days as {
                    limit: number;
                    offset: number;
                    days: number;
                  }
                ).days
              }`
            : ""
        }`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getBusinessPartnerUsersChart: builder.query<
      {
        date_time: number;
        count: number;
      }[],
      number
    >({
      query: (days) => ({
        url: `/partner/business-partner/users/chart${`?days=${days}`}`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getBusinessPartnerUsersStatus: builder.query<any, void>({
      query: (days) => ({
        url: `/partner/business-partner/users/status`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getInterfaceMessage: builder.query<
      {
        en: {
          image_link: string;
          video_link: string;
          message_date_time: string; // You can use a `Date` type if you plan to parse this into a Date object
          message: string;
          cta: string;
          level: "info" | "warning" | "error"; // Assuming `level` has predefined values
        }[];
        fr: {
          image_link: string;
          video_link: string;
          message_date_time: string; // You can use a `Date` type if you plan to parse this into a Date object
          message: string;
          cta: string;
          level: "info" | "warning" | "error"; // Assuming `level` has predefined values
        }[];
      },
      void
    >({
      query: () => ({
        url: `/server/client/interface-message`,
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element?.messages
    }),
    updateProfileBusinessPartner: builder.mutation<any, any>({
      query: (data) => ({
        url: `/partner/business-partner/update-info`,
        method: "PUT",
        headers: {
          "x-auth-token": getToken()
        },
        data: data
      }),
      transformResponse: (response: any) => response.element,
      invalidatesTags: ["GetBusinessPartner"]
    }),
    providerAPIKey: builder.mutation<
      { provider: "openai" | "anthropic"; apiKey: string },
      any
    >({
      query: (data: { provider: "openai" | "anthropic"; apiKey: string }) => ({
        url: `/kitty/provider-api-key`,
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        },
        data: data
      }),
      transformResponse: (response: any) => response.element
    }),
    setApiKey: builder.mutation<{ openai: string; anthropic: string }, any>({
      query: (data: { openai: string; anthropic: string }) => ({
        url: `/server/client/setting/apikey`,
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        },
        data: {
          apiKeys: data
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    exportChatbotHistory: builder.mutation<
      any,
      { bot_id: string; ids: number[] }
    >({
      query: (data: { bot_id: string; ids: number[] }) => ({
        url: `/statistics/chat-history/export`,
        method: "POST",
        headers: {
          "x-auth-token": getToken()
        },
        data: data
      }),
      transformResponse: (response: any) => response.element
    }),
    getFirstInteractionChart: builder.query<
      any,
      { from: string; to: string; botId: string }
    >({
      query: ({ from, to, botId: bot_id }) => ({
        url: `/statistics/first-interaction-chart`,
        params: { from, to, bot_id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getConversationChart: builder.query<
      any,
      { from: string; to: string; bot_id: string }
    >({
      query: ({ from, to, bot_id }) => ({
        url: `/statistics/conversation-chart`,
        params: { from, to, bot_id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getTimeZoneChart: builder.query<
      any,
      { from: string; to: string; bot_id: string }
    >({
      query: ({ from, to, bot_id }) => ({
        url: `/statistics/time-zone-chart`,
        params: { from, to, bot_id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getBrowserLanguageChart: builder.query<
      any,
      { from: string; to: string; bot_id: string }
    >({
      query: ({ from, to, bot_id }) => ({
        url: `/statistics/browser-language-chart`,
        params: { from, to, bot_id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getDeviceTypeChart: builder.query<
      any,
      { from: string; to: string; bot_id: string }
    >({
      query: ({ from, to, bot_id }) => ({
        url: `/statistics/device-type-chart`,
        params: { from, to, bot_id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    getConversationHistory: builder.query<
      any,
      { botId: string; page: number; limit: number }
    >({
      query: ({ botId, page, limit }) => ({
        url: `/statistics/chat-histories`,
        params: { bot_id: botId, page, limit },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      providesTags: ["ConversationHisyory"],
      transformResponse: (response: any) => response.element
    }),
    getConversationHistoryDetail: builder.query<
      any,
      { botId: string; id: string }
    >({
      query: ({ botId, id }) => ({
        url: `/statistics/chat-history`,
        params: { bot_id: botId, id },
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    deleteConversationHistory: builder.mutation<
      any,
      { ids: any; bot_id: string }
    >({
      query: (data) => ({
        url: `/statistics/chat-history`,
        method: "DELETE",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["ConversationHisyory"]
    }),
    deleteConversationHistoryAll: builder.mutation<any, { bot_id: string }>({
      query: (data) => ({
        url: `/statistics/chat-histories`,
        method: "DELETE",
        data: data,
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: ["ConversationHisyory"]
    }),
    getMessageSounds: builder.query<
      Array<{ title: string; file: string; cdnPath: string }>,
      void
    >({
      query: () => ({
        url: "/notification-sounds",
        method: "GET",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response: any) => response.element
    }),
    RescanBot: builder.mutation<any, string>({
      query: (id) => ({
        url: `/v3/knowledgeBase/rescan/${id}`,
        method: "PATCH",
        headers: {
          "x-auth-token": getToken()
        }
      }),
      transformResponse: (response) => response.element,
      invalidatesTags: (result) =>
        result ? ["GetAllScenarios", "Package"] : []
    })
  })
});

export const {
  useGetPackageQuery,
  useGetAllScenariosQuery,
  useLazyGetAllScenariosQuery,
  useGetScriptQuery,
  useSetDefaultScenariosMutation,
  useDeleteScenarioMutation,
  useCreateContactUsMutation,
  useGetBotsQuery,
  useCreateAssistantBotMutation,
  useGetAssistantDetailMutation,
  useEditAssistantBotMutation,
  useCreateKbAssistantBotMutation,
  useEditKbAssistantBotMutation,
  useAddFileKbAssistantBotMutation,
  useDeleteFileKbAssistantBotMutation,
  useUpdateWidgetSettingMutation,
  useGetAssistantsQuery,
  useGetTextScenarioStarterKitsQuery,
  useLazyGetVoicePopoverQuery,
  useUpdateAiWidgetAppearanceMutation,
  useUpdateMcBotWidgetAppearanceMutation,
  useDeactivateAiTypeMutation,
  useDeactivateMcBotTypeMutation,
  useUploadToCDNMutation,
  useLazyUrlCheckerQuery,
  useGenerateUploadUrlMutation,
  useAddKbAssistantBotMutation,
  useValidateUploadedItemMutation,
  useGetBusinessPartnerUsersQuery,
  useGetBusinessPartnerQuery,
  useGetBusinessPartnerUsersChartQuery,
  useUpdateProfileBusinessPartnerMutation,
  useGetBusinessPartnerUsersStatusQuery,
  useGetInterfaceMessageQuery,
  useGetFirstInteractionChartQuery,
  useGetTimeZoneChartQuery,
  useGetBrowserLanguageChartQuery,
  useGetDeviceTypeChartQuery,
  useProviderAPIKeyMutation,
  useSetApiKeyMutation,
  useGetConversationHistoryQuery,
  useGetConversationHistoryDetailQuery,
  useExportChatbotHistoryMutation,
  useGetMessageSoundsQuery,
  useDeleteConversationHistoryMutation,
  useDeleteConversationHistoryAllMutation,
  useRescanBotMutation,
  useKnowledgeBaseDuplicateMutation,
  useGetCdnFileInfoMutation
} = ChatbotRTKService;
